/**
 * ChatListHeaderRow — the "Direct messages" section header of the Inbox list.
 *
 * Flutter source of truth: `ChatListHeader`
 * (`lib/presentation/screens/messages/widgets/chat_list_header.dart:16-34`).
 *
 * Renders nothing when the chat list is both empty and exhausted
 * (`!hasMoreItems && !hasItems`); otherwise a left-aligned "Direct messages"
 * label. The top padding collapses to 0 when the notifications list above is
 * empty (`notificationsEmpty`) so the two sections do not double-space.
 *
 * Token mapping (Flutter padding `left:16, top: notificationsEmpty ? 0 : 32`,
 * `chatHeaderStyle`: `disabledTextColor`, size 14, weight 400):
 *   - `left:16`   → `responsive.spacing.lg` (16).
 *   - `top:32`    → `responsive.spacing['3xl']` (32 — exact match).
 *   - `top:0`     → 0.
 *   - color       → `theme.text.secondary`.
 *   - size 14     → `responsive.font.sm`.
 *   - weight 400  → `responsive.fontWeight.regular`.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

export interface ChatListHeaderRowProps {
  notificationsEmpty: boolean;
  hasMoreItems: boolean;
  hasItems: boolean;
}

export const ChatListHeaderRow: React.FC<ChatListHeaderRowProps> = ({
  notificationsEmpty,
  hasMoreItems,
  hasItems,
}) => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter renders `Container()` (nothing) when empty-and-exhausted.
  if (!hasMoreItems && !hasItems) return null;

  return (
    <div
      style={{
        paddingLeft: responsive.spacing.lg,
        paddingTop: notificationsEmpty ? 0 : responsive.spacing['3xl'],
      }}
    >
      <span
        style={{
          display: 'block',
          textAlign: 'left',
          color: theme.text.secondary,
          fontSize: responsive.font.sm,
          fontWeight: responsive.fontWeight.regular,
        }}
      >
        {l10n.inbox.directMessagesHeader}
      </span>
    </div>
  );
};
